import React, { useMemo } from 'react';
import EnsureId from 'psims/react/pages/primary-pages/data-submissions/shared/ensure-id';
import LoadingSubmission from 'psims/react/pages/primary-pages/data-submissions/shared/loading-submission';
import LoadSubmissionFailed from 'psims/react/pages/primary-pages/data-submissions/shared/load-submission-failed';
import useMsoRefinerApi, { UseMsoRefinerAPI } from './use-mso-refiner-api';
import MsoRefinerEditor from './mso-refiner-editor';
import { useDashboard } from 'psims/react/providers/api/dashboard';
import { SubmissionStatus } from '../../shared/api';

interface MsoRefinerProps {
    id: number;
}
type LoadState = 'loading' | 'loaded' | 'error';

// Define type for dashboard submissions
type MsoRefinerSubmission = {
    id: number;
    caseId: string;
    reportingOrganisationId: number;
    reportingOrganisationName: string;
};

// Hook to find previous submission based on caseId
function usePreviousId(
    msoRefinerW: MsoRefinerSubmission[],
    currentId: number
) {
    return useMemo<number>(() => {
        if (!msoRefinerW?.length) return currentId;

        const current = msoRefinerW.find(msi => msi.id === currentId);
        if (!current) return currentId;

        const parseCaseId = (caseId: string) => {
            const parts = caseId.split('-');
            if (parts.length !== 3) return null;
            const [type, periodStr, seqStr] = parts;
            const period = parseInt(periodStr, 10);
            const seq = parseInt(seqStr, 10);
            if (isNaN(period) || isNaN(seq)) return null;
            return { type, period, seq };
        };

        const currentParsed = parseCaseId(current.caseId);
        if (!currentParsed) return currentId;

        const previous = msoRefinerW
            .filter(msi => {
                const parsed = parseCaseId(msi.caseId);
                return (
                    parsed &&
                    parsed.type === currentParsed.type &&
                    msi.reportingOrganisationId === current.reportingOrganisationId &&
                    (parsed.period < currentParsed.period ||
                     (parsed.period === currentParsed.period && parsed.seq < currentParsed.seq))
                );
            })
            .sort((a, b) => {
                const pa = parseCaseId(a.caseId)!;
                const pb = parseCaseId(b.caseId)!;
                return pb.period - pa.period || pb.seq - pa.seq;
            })[0];

        return previous?.id ?? currentId;
    }, [msoRefinerW, currentId]);
}

const MsoRefiner = (props: MsoRefinerProps) => {
    const vm = useVM(props);

    switch(vm.loadState) {
        case 'error':
            return <LoadSubmissionFailed submissionName='MSO refiner' />
        case 'loaded':
            return <MsoRefinerEditor
                apiCtrl={vm.apiCtrl as PopulatedProps<UseMsoRefinerAPI, 'submission'>}
                prevApiCtrl={vm.prevApiCtrl as PopulatedProps<UseMsoRefinerAPI, 'submission'>}
            />;
        default:
            return <LoadingSubmission submissionName='MSO refiner' />;
    }
};

function useVM(props: MsoRefinerProps) {
    const { msoRefinerW: rawData } = useDashboard();

    // Cast to proper type
const msoRefinerW: MsoRefinerSubmission[] = (rawData ?? [])
    .filter(
        (msi): msi is MsoRefinerSubmission =>
            typeof msi.id === 'number' &&
            typeof msi.caseId === 'string' &&
            typeof msi.reportingOrganisationId === 'number' &&
            typeof msi.reportingOrganisationName === 'string'
    );
    const previousId = usePreviousId(msoRefinerW, props.id);

    const apiCtrl = useMsoRefinerApi({ id: props.id });
    const prevApiCtrl = useMsoRefinerApi({ id: previousId });

    const isLoadedStatus = (status: SubmissionStatus) =>
        status !== 'init' &&
        status !== 'fetching' &&
        status !== 'fetch_failed';

    const bothFetched =
        isLoadedStatus(apiCtrl.loadStatus) &&
        isLoadedStatus(prevApiCtrl.loadStatus);

    const loadState: LoadState =
        apiCtrl.loadStatus === 'fetch_failed' || prevApiCtrl.loadStatus === 'fetch_failed'
            ? 'error'
            : bothFetched
            ? 'loaded'
            : 'loading';

    if (!bothFetched) {
        return {
            apiCtrl: null,
            prevApiCtrl: null,
            loadState,
        };
    }

    if (!apiCtrl.submission || !prevApiCtrl.submission) {
        return {
            apiCtrl: null,
            prevApiCtrl: null,
            loadState: 'error',
        };
    }

    return {
        apiCtrl: apiCtrl as PopulatedProps<UseMsoRefinerAPI, 'submission'>,
        prevApiCtrl: prevApiCtrl as PopulatedProps<UseMsoRefinerAPI, 'submission'>,
        loadState: 'loaded',
    };
}

const Page = (props: MsoRefinerProps) => <EnsureId id={props.id} Component={MsoRefiner} />;

export default Page;